import React, { useEffect, useRef, useState } from "react";
import { Link, Route, Routes } from "react-router-dom";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import ShowIcon from "@mui/icons-material/Description";
import AttachmentIcon from "@mui/icons-material/Attachment";
import { makeStyles } from "tss-react/mui";
import { User } from "./App";
import EditSong from "./EditSong";
import ShowSong from "./ShowSong";

import config from "./config.json";
import { useFeedback } from "./Feedback";
import { HTTP, HTTPMethod } from "./Backend";
import ConfirmDialog from "./ConfirmDialog";

interface SongProperties {
    user: User;
}

const useStyles = makeStyles()((theme) => ({
    song: {
        margin: theme.spacing(1)
    },
    datagrid: {
        color: theme.palette.text.secondary
    },
    songNameLink: {
        color: "inherit",
        textDecoration: "underline",
    },
    buttoninput: {
        margin: theme.spacing(1)
    }    
}));

export interface SongItem {
    id: number;
    songname: string;
    album: string;
    date: Date;
    comments: string;
    attachments: AttachmentItem[];
    /** Synthetic column for action buttons. */
    action: string;
}

interface AttachmentItem {
    attachmentid: number;
    attachmentfilename: string;
}

function Song(props: SongProperties) {

  const { classes } = useStyles();

  const [busy, setBusy] = useState<boolean>(false);
  const [songs, setSongs] = useState<SongItem[]>([]);
  const [currentSongId, setCurrentSongId] = useState<number | null>(null);
  const [reloadRequest, setReloadRequest] = useState<boolean>(true);
  const [deleteCandidateId, setDeleteCandidateId] = useState<number | null>(null);
  const [attachmentAnchorEl, setAttachmentAnchorEl] = useState<null | HTMLElement>(null);
  const [attachmentMenuItems, setAttachmentMenuItems] = useState<AttachmentItem[]>([]);

  const feedback = useRef(useFeedback());
  
  useEffect(() => {
        if (reloadRequest) {
            setBusy(true);
            HTTP(HTTPMethod.GET, config.urlSongs)
            .then(json => {
                    let newSongs: SongItem[] = [];
                    for (let record of json) {
                        const row:SongItem = {
                        id: record.songid,
                        songname: record.songname,
                        album: record.album ?? "",
                        date: record.date,
                        comments: record.comments,
                        attachments: record.attachments ?? [],
                        action: "",
                        };
                        newSongs.push(row);
                    }
                    setSongs(newSongs);
            })
            .catch(error => {
                feedback.current(error);
            })
            .finally(() => {
                setBusy(false);
                setReloadRequest(false);
            });
        }
  }, [reloadRequest]);

  const deleteSong = (id: number | null) => {
    setBusy(true);
    HTTP(HTTPMethod.DELETE, config.urlDeleteSong + '/' + id, props.user.token)
    .then(() => {
        setReloadRequest(true);
    })
    .catch(error => {
        feedback.current(error);
    })
    .finally(() => {
        setBusy(false);
    });
  };

  const handleOpenAttachmentsMenu = (
    event: React.MouseEvent<HTMLElement>,
    attachments: AttachmentItem[]
  ) => {
    setAttachmentAnchorEl(event.currentTarget);
    setAttachmentMenuItems(attachments);
  };

  const handleCloseAttachmentsMenu = () => {
    setAttachmentAnchorEl(null);
    setAttachmentMenuItems([]);
  };

  const columns: GridColDef<SongItem>[] = [
    { field: "id", headerName: "#", width: 100 },
    { field: "songname", headerName: "Nummer", width: 240,
      renderCell: (params: GridRenderCellParams<SongItem>) => (
        <Link className={classes.songNameLink} to={"/sheets/" + params.row.id}>
          {params.row.songname}
        </Link>
      )
    },
    { field: "album", headerName: "Album", width: 160 },
    { field: "date", headerName: "Datum", width: 120 },
    { field: "comments", headerName: "Opmerkingen", width: 360 },
    { field: "action", headerName: "Actie", width: 320, sortable: false,
      renderCell: (params: GridRenderCellParams<SongItem>) => (
        <span>
          <Tooltip title="Bewerk akkoordenschema">
            <span>
                <IconButton
                    color="secondary"
                    className={classes.buttoninput}
                    disabled={props.user.token === null}
                    onClick={() => {setCurrentSongId(params.row.id as number);}}
                > 
                    <EditIcon/>
                </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Akkoordenschema bekijken">
            <Link to={"/sheets/" + params.row.id}>
              <IconButton
                color="secondary"
                className={classes.buttoninput}
              > 
                <ShowIcon/>
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title="Download akkoordenschema als PDF">
            <IconButton
                color="secondary"
                className={classes.buttoninput}
                href={config.urlGetPdfSong + "/" + params.row.id}
            > 
                <PdfIcon/>
            </IconButton>
          </Tooltip>
          <Tooltip title={params.row.attachments.length > 0 ? `Bekijk bijlage (${params.row.attachments.length})` : "Geen bijlage"}>
            <span>
              <IconButton
                  color="secondary"
                  className={classes.buttoninput}
                  disabled={params.row.attachments.length === 0}
                  onClick={(event) => handleOpenAttachmentsMenu(event, params.row.attachments)}
              >
                  <Badge badgeContent={params.row.attachments.length} color="secondary">
                      <AttachmentIcon/>
                  </Badge>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Verwijder dit nummer">
            <span>
                <IconButton
                    color="secondary"
                    className={classes.buttoninput}
                    disabled={props.user.token === null}
                    onClick={() => setDeleteCandidateId(params.row.id as number)}
                >
                    <DeleteIcon/>
                </IconButton>
            </span>
          </Tooltip>
        </span>
      )
    }
  ];


  const handleAddSong = () => {
      setCurrentSongId(0);
  }

  const handleCloseEditDialog = () => {
      setReloadRequest(true);  // Force reload of the data.
      setCurrentSongId(null);
  }

  return (
      <section className={classes.song}>

        <Routes>
            <Route path="/" element={
                <>
                <EditSong songid={currentSongId} user={props.user} onClose={handleCloseEditDialog}/>

                <div style={{ height: 800, width: '100%' }}>
                    <DataGrid
                    className={classes.datagrid}
                    loading={busy}
                    rows={songs} 
                    columns={columns}
                    autoPageSize
                    initialState={{
                        sorting: { sortModel: [{ field: "songname", sort: "asc" }] },
                    }}
                    />
                </div>

                <Button
                    className={classes.buttoninput}
                    color="secondary"
                    variant="contained"
                    disabled={props.user.token === null}
                    onClick={handleAddSong}>
                        Nieuw nummer toevoegen
                </Button>

                {
                    /*
                        <Button 
                            className={classes.buttoninput}
                            color="secondary"
                            variant="contained"
                            href={config.urlSongbook}>
                                Download songbook
                        </Button>
                    */
                }
                </>
            }/>
            
            <Route path=":songid" element={<ShowSong/>}/>
        </Routes>

        <ConfirmDialog
            title="Bevestig verwijderen nummer"
            open={deleteCandidateId !== null}
            onConfirm={() => {deleteSong(deleteCandidateId)}}
            onClose={() => setDeleteCandidateId(null)}
        >
            Weet je zeker dat je dit nummer wilt verwijderen?
        </ConfirmDialog>

        <Menu
            anchorEl={attachmentAnchorEl}
            open={Boolean(attachmentAnchorEl)}
            onClose={handleCloseAttachmentsMenu}
        >
            {attachmentMenuItems.map((attachment) => (
                <MenuItem
                    key={attachment.attachmentid}
                    component="a"
                    href={config.urlGetAttachment + "/" + attachment.attachmentid}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleCloseAttachmentsMenu}
                >
                    {attachment.attachmentfilename}
                </MenuItem>
            ))}
        </Menu>

      </section>
  );
}

export default Song;
